import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Text } from '@fragments';
import {
  IconAlertCircle, IconArrowBackUp, IconArrowForwardUp, IconCheck, IconCloudOff, IconHistory, IconMinus, IconMovie, IconPalette, IconPhoto, IconPlus, IconRefresh,
  IconShape, IconStack2, IconTypography,
} from '@tabler/icons-react';
import { useEnter } from '../../motion';
import './styles.scss';

// `videos` (2.13.0) is listed only where the host takes videos and the format plays them.
export const TOOLS = [
  { id: 'text', icon: IconTypography, label: 'toolText' },
  { id: 'elements', icon: IconShape, label: 'toolElements' },
  { id: 'photos', icon: IconPhoto, label: 'toolPhotos' },
  { id: 'videos', icon: IconMovie, label: 'toolVideos' },
  { id: 'brand', icon: IconPalette, label: 'toolBrand' },
  { id: 'layers', icon: IconStack2, label: 'toolLayers' },
];
const FLASH_MS = 1600;

// The version on screen, which also tells whether it is saved: a dot while edits wait for
// the autosave, a turning ring while they save, a check for a moment once saved, a red
// ring when saving failed (its menu then starts with "try again"), a cloud when offline.
function VersionButton({ versions, status }) {
  const { t } = useTranslation('artEditor');
  const state = status?.state || 'saved';
  const [flash, setFlash] = useState(false);
  const previous = useRef(state);
  useEffect(() => {
    const was = previous.current;
    previous.current = state;
    if (state !== 'saved' || !['saving', 'pending'].includes(was)) return undefined;
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), FLASH_MS);
    return () => clearTimeout(timer);
  }, [state]);
  const Icon = state === 'error' ? IconAlertCircle : state === 'offline' ? IconCloudOff : flash ? IconCheck : IconHistory;
  const icon = useEnter(Icon, { scale: 0.6 }, { duration: 0.18 });
  const statusLabel = status ? t(`status_${state}`) : null;
  const retry = state === 'error' && status?.onRetry ? [{ label: `${t('status_error')} · ${t('retry')}`, icon: <IconRefresh />, onClick: status.onRetry }, { divider: true }] : [];
  return <>
    <Menu items={[...retry, ...versions.items]} placement="right-start">
      <button type="button" className={`art-tool is-version is-${state}${flash ? ' is-flash' : ''}`} title={statusLabel ? `${versions.label} · ${statusLabel}` : t('versions')}
        aria-label={`${t('versions')}: ${versions.label}${statusLabel ? ` · ${statusLabel}` : ''}`}>
        <span ref={icon} className="art-tool__icon"><Icon aria-hidden="true" /></span>
        <Text.caption contrast={8}>{versions.label}</Text.caption>
        {state === 'pending' && <span className="art-tool__dot" aria-hidden="true" />}
      </button>
    </Menu>
    {/* Announced as it changes (the button shows it as marks). */}
    {statusLabel && <span className="art-tools__live" role="status">{statusLabel}</span>}
  </>;
}

// The left rail, in three parts: the version on screen (which shows whether it is saved) on top,
// the tools that add to the art in the middle, history and zoom at the bottom. It replaces
// a header bar, so the art keeps the whole height.
export default function ToolRail({ active, onToggle, disabled = false, versions, status, history, zoom, hidden = [] }) {
  const { t } = useTranslation('artEditor');
  return <nav className="art-tools" aria-label={t('tools')}>
    <div className="art-tools__top">
      {versions?.items?.length > 0 && <VersionButton versions={versions} status={status} />}
    </div>
    <div className="art-tools__main">
      {TOOLS.filter(tool => !hidden.includes(tool.id)).map(tool => <button key={tool.id} type="button" className={`art-tool ${active === tool.id ? 'is-active' : ''}`} aria-pressed={active === tool.id}
        aria-controls="art-editor-panel" disabled={disabled} onClick={() => onToggle(tool.id)}>
        <tool.icon aria-hidden="true" />
        <Text.caption contrast={active === tool.id ? 9 : 7}>{t(tool.label)}</Text.caption>
      </button>)}
    </div>
    {(history || zoom) && <div className="art-tools__dock" role="group" aria-label={t('view')}>
      {history && <div className="art-tools__pair">
        <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconArrowBackUp />} disabled={!history.canUndo} aria-label={t('undo')} tooltip={t('undoShortcut')} onClick={history.undo} />
        <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconArrowForwardUp />} disabled={!history.canRedo} aria-label={t('redo')} tooltip={t('redoShortcut')} onClick={history.redo} />
      </div>}
      {history && zoom && <span className="art-tools__divider" aria-hidden="true" />}
      {zoom && <>
        <button type="button" className="art-tools__zoom" onClick={zoom.onFit} title={t('zoomFit')} aria-label={`${t('zoom')}: ${zoom.label} · ${t('zoomFit')}`}>
          <Text.caption contrast={7}>{zoom.label}</Text.caption>
        </button>
        <div className="art-tools__pair is-zoom">
          <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconMinus />} aria-label={t('zoomOut')} tooltip={t('zoomOut')} onClick={zoom.onOut} />
          <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconPlus />} aria-label={t('zoomIn')} tooltip={t('zoomIn')} onClick={zoom.onIn} />
        </div>
      </>}
    </div>}
  </nav>;
}
